import { Box, Button, useMediaQuery } from "@mui/material";
import { Tab, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { styles } from "./styles";
import { ExplorePageProps, TrackToPlay } from "./types";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { ExploreTracks } from "../../components/Exolore/ExploreTracks";
import { ExploreArtists } from "../../components/Exolore/ExploreArtists";
import { enterpriseName } from "../../theme";
import { APP_BACKGROUND_COLOR } from "../../theme";
import { isEnterprise } from "../../theme";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import { AudioPlayer } from "../../components/TracksPageComponents/AudioPlayer";
import { GlobalSearchBar } from "../../components/ResponsiveNavbar/GlobalSearchBar";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { GetPublishedTracksResponse } from "../../proto/api_pb";
import { GetTrackResponse } from "../../proto/api_pb";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { getPublishedTrack } from "../../services/trackService";
import { sortOptions } from "../../components/ExploreBase/types";
import { useAuth0 } from "@auth0/auth0-react";
import { ExploreTracksSearchBar } from "../../components/Exolore/ExploreTracksSearchBar";
import { ExploreFilterMenu } from "../../components/Exolore/ExploreFilterMenu";
import { ExploreSortMenu } from "../../components/Exolore/ExploreSortMenu";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";

export const ExplorePage: React.FC<ExplorePageProps> = ({
  session,
  setInbox,
}) => {
  const [value, setValue] = useState<string>("1");
  const [allowDisplaySearchBar, setAllowDisplaySearchBar] =
    useState<boolean>(true);
  const [isPlayButtonClicked, setIsPlayButtonClicked] =
    useState<boolean>(false);
  const [trackToPlay, setTrackToPlay] = useState<TrackToPlay>();
  const [queryValue, setQueryValue] = useState<string>("");
  const [genres, setGenres] = useState<string[]>([]);
  const [featuredTracks, setFeaturedTracks] = useState<GetTrackResponse[]>([]);
  const [publicTracks, setPublicTracks] = useState<GetTrackResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFeaturedTracksLoaded, setIsFeaturedTracksLoaded] =
    useState<boolean>(false);
  const [sort, setSort] = useState(sortOptions[0]);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const savedTab = sessionStorage.getItem("selectedTab");
    setValue(savedTab || "1");
  }, []);

  const { page, scrollContainerRef, setIsScrollEnabled, setPage } =
    useInfiniteScroll();

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    sessionStorage.setItem("selectedTab", newValue);
  };

  useEffect(() => {
    const getFeaturedTracks = async () => {
      const limit = 8;
      const isFeaturedArtistTrack = true;
      const response = await getPublishedTrack(
        page,
        limit,
        [],
        sort,
        isFeaturedArtistTrack,
        "",
        queryValue
      );
      if (response instanceof GetPublishedTracksResponse) {
        if (response.getTracksList().length < 8) {
          setIsFeaturedTracksLoaded(true);
          setPage(0);
        }
        setFeaturedTracks((prevTracks) => [
          ...prevTracks,
          ...response.getTracksList(),
        ]);
        setIsScrollEnabled(true);
      }
    };
    if (!isFeaturedTracksLoaded) {
      getFeaturedTracks();
    }
  }, [
    isFeaturedTracksLoaded,
    page,
    setIsScrollEnabled,
    setPage,
    genres,
    sort,
    queryValue,
  ]);

  const getPublishedTracks = useCallback(async () => {
    try {
      const limit = 8;
      const isFeaturedArtistTrack = false;
      const response = await getPublishedTrack(
        page,
        limit,
        genres,
        sort,
        isFeaturedArtistTrack,
        "",
        queryValue
      );
      if (response instanceof GetPublishedTracksResponse) {
        if (response.getTracksList().length === 0) {
          setIsScrollEnabled(false);
          setLoading(false);
        } else {
          setIsScrollEnabled(true);
        }

        setPublicTracks((prevTracks) => [
          ...prevTracks,
          ...response.getTracksList(),
        ]);
      }
    } catch (er) {
      console.log(er);
    }
  }, [genres, page, queryValue, setIsScrollEnabled, sort]);

  useEffect(() => {
    document.title = isEnterprise ? `${enterpriseName}` : "OmMuse";
    if (isFeaturedTracksLoaded) getPublishedTracks();
  }, [getPublishedTracks, isFeaturedTracksLoaded]);

  const handleChangeGenre = async (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    setIsFeaturedTracksLoaded(true);
    setFeaturedTracks([]);
    setPublicTracks([]);
    setPage(0);
    const values = value as Array<string>;
    setGenres(values);
    if (values.length === 0) {
      setIsFeaturedTracksLoaded(false);
    }
  };

  const handleSort = (sortValue: string) => {
    setPublicTracks([]);
    setFeaturedTracks([]);
    setIsFeaturedTracksLoaded(false);
    setPage(0);
    setSort(sortValue);
  };

  return (
    <>
      {isAuthenticated && <MobileDrawer />}
      <AudioPlayer
        trackToPlay={trackToPlay}
        isPlayButtonClicked={isPlayButtonClicked}
        isTrackForPlay={true}
        versionToPlay={undefined}
        audioDetails={undefined}
        setTrackToPlay={setTrackToPlay}
        setIsPlayButtonClicked={setIsPlayButtonClicked}
      />
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: "100%",
            padding: "15px",
            paddingLeft: isSmallScreen ? "15px" : "30px",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: isSmallScreen ? "10px" : 0,
              }}
            >
              <Typography sx={{ fontWeight: "bold" }} variant="h4">
                Explore
              </Typography>
              {!isAuthenticated && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    marginTop: isSmallScreen ? -1.5 : 0,
                  }}
                >
                  <Button
                    onClick={() => {
                      loginWithRedirect({
                        appState: { returnTo: "/my-tracks" },
                        authorizationParams: { screen_hint: "login" },
                      });
                    }}
                    sx={styles.button}
                  >
                    Log in
                  </Button>
                  <Button
                    onClick={() => {
                      loginWithRedirect({
                        appState: { returnTo: "/my-tracks" },
                        authorizationParams: { screen_hint: "signup" },
                      });
                    }}
                    sx={styles.button}
                  >
                    Sign up
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={styles.filterSortContainer}>
              {value === "1" && (
                <ExploreTracksSearchBar
                  setFeaturedTracks={setFeaturedTracks}
                  setPublicTracks={setPublicTracks}
                  setPage={setPage}
                  queryValue={queryValue}
                  setQueryValue={setQueryValue}
                  setIsFeaturedTracksLoaded={setIsFeaturedTracksLoaded}
                  setIsScrollEnabled={setIsScrollEnabled}
                />
              )}
              {value !== "1" && isAuthenticated && (
                <GlobalSearchBar
                  userId={userId}
                  allowDisplaySearchBar={allowDisplaySearchBar}
                  setAllowDisplaySearchBar={setAllowDisplaySearchBar}
                  isArtistSearchBar={true}
                />
              )}
              <ExploreFilterMenu
                genres={genres}
                handleChangeGenre={handleChangeGenre}
              />
              {value !== "2" && <ExploreSortMenu handleSort={handleSort} />}
            </Box>
          </Box>
          <Box
            sx={{
              ...styles.tabContainer,
              ...{ marginTop: isSmallScreen ? 0 : "18px" },
            }}
          >
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: APP_BACKGROUND_COLOR }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                >
                  <Tab sx={styles.tab} label="Tracks" value="1" />
                  <Tab sx={styles.tab} label="Artists" value="2" />
                </TabList>
              </Box>
              <TabPanel value="1" sx={{ padding: 0, margin: 0 }}>
                <ExploreTracks
                  setTrackToPlay={setTrackToPlay}
                  featuredTracks={featuredTracks}
                  publicTracks={publicTracks}
                  loading={loading}
                  isFeaturedTracksLoaded={isFeaturedTracksLoaded}
                  scrollContainerRef={scrollContainerRef}
                  genres={genres}
                  trackToPlay={trackToPlay}
                  setIsPlayButtonClicked={setIsPlayButtonClicked}
                  session={session}
                  setInbox={setInbox}
                />
              </TabPanel>
              <TabPanel value="2" sx={{ padding: 0, margin: 0 }}>
                <ExploreArtists
                  genres={genres}
                  session={session}
                  setInbox={setInbox}
                />
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Box>
      {isAuthenticated && (
        <MobileScreenBottomNavigationContainer>
          <MobileScreenBottomNavigation />
        </MobileScreenBottomNavigationContainer>
      )}
    </>
  );
};
